import { useTranslation } from 'react-i18next'
import { Base } from '@itsrever/design-system'
import {
  OutcomeRule,
  RDV_SURPLUS_TYPES,
  RDVSurplusRule,
  RDVSurplusType,
  REFUND_CONFIGURATION_ACTIONS,
  REFUND_CONFIGURATION_METHODS,
  REFUND_TIMINGS,
  RefundsSettings,
  invalidRDVSurplusRules,
  parseSurplusAmount,
  parseSurplusPercentage
} from '@/domain/refund'
import {
  FormProps,
  SettingsPanel,
  SettingsSection,
  SwitchRow
} from '@/ui/components/group-settings'
import { whensComplete } from '@/domain/warehouses'
import { currencySymbol } from '@/utils/format'
import { Column, PickCell, RuleTable } from './rule-table'
import { DecimalInput } from './decimal-input'

const { FieldRoot, ToggleGroup, ToggleGroupItem } = Base

const DEFAULT_TIMING = 'ON_ITEM_VERIFIED'

const DISCOUNTS = ['roundPromocode', 'revokeConditionalDiscounts'] as const

export const useRefundsProblem = () => {
  const { t } = useTranslation()
  return (refunds: RefundsSettings) => {
    if (invalidRDVSurplusRules(refunds.rdvSurplus).length > 0)
      return t('returns_refunds.refunds.rdv_surplus_problem')
    if (
      [...refunds.methods, ...refunds.actions].some(
        (rule) => !rule.timing && rule.target !== 'TO_RETURN'
      )
    )
      return t('returns_refunds.refunds.timing_problem')
    return [
      ...refunds.methods,
      ...refunds.actions,
      ...(refunds.rdvSurplus ?? [])
    ].every((rule) => whensComplete(rule.whens))
      ? undefined
      : t('returns_refunds.incomplete_rules')
  }
}

export const RefundsForm: React.FC<FormProps<RefundsSettings>> = ({
  value,
  saved,
  readOnly,
  onChange
}) => {
  const { t, i18n } = useTranslation()
  const options = (values: string[], prefix: string) =>
    values.map((v) => ({ value: v, label: t(`${prefix}.${v}`) }))
  const columns = (
    label: string,
    targets: { value: string; label: string }[],
    // Whether a rule may leave its timing unset; settings-api requires one for methods and exchanges.
    timingOptional: (target: string) => boolean = () => false
  ): Column<OutcomeRule>[] => [
    {
      label,
      className: 'w-48',
      render: (rule, update, cell) => (
        <PickCell
          id={`${cell.id}:target`}
          label={t('returns_refunds.refunds.target_for', { label, n: cell.n })}
          options={targets}
          value={rule.target}
          unsetLabel={t('returns_refunds.refunds.timings.unset')}
          readOnly={readOnly}
          openKey={cell.openKey}
          setOpenKey={cell.setOpenKey}
          onPick={(target) =>
            update({
              ...rule,
              target,
              timing:
                rule.timing || timingOptional(target)
                  ? rule.timing
                  : DEFAULT_TIMING
            })
          }
        />
      )
    },
    {
      label: t('returns_refunds.refunds.timing'),
      className: 'w-48',
      render: (rule, update, cell) => (
        <PickCell
          id={`${cell.id}:timing`}
          label={t('returns_refunds.refunds.timing_for', { n: cell.n })}
          options={[
            ...(timingOptional(rule.target)
              ? [
                  {
                    value: '',
                    label: t('returns_refunds.refunds.timings.unset')
                  }
                ]
              : []),
            ...options(REFUND_TIMINGS, 'returns_refunds.refunds.timings')
          ]}
          value={rule.timing}
          unsetLabel={t('returns_refunds.refunds.timings.unset')}
          readOnly={readOnly}
          openKey={cell.openKey}
          setOpenKey={cell.setOpenKey}
          onPick={(timing) => update({ ...rule, timing })}
        />
      )
    }
  ]
  const methodsLabel = t('returns_refunds.refunds.methods_title')
  const actionsLabel = t('returns_refunds.refunds.actions_title')
  const surplusLabel = t('returns_refunds.refunds.rdv_surplus_title')
  const badSurplus = invalidRDVSurplusRules(value.rdvSurplus)
  const surplusColumns: Column<RDVSurplusRule>[] = [
    {
      label: t('returns_refunds.refunds.rdv_surplus_extra'),
      className: 'flex w-80 items-center gap-2',
      render: (rule, update, { n }) => (
        <FieldRoot
          invalid={badSurplus.includes(n - 1)}
          className="flex-row items-center gap-2"
        >
          <ToggleGroup
            variant="button"
            aria-label={t('returns_refunds.refunds.rdv_surplus_type_for', {
              n
            })}
            value={[rule.type]}
            disabled={readOnly}
            // A euro amount means nothing as a percentage, so a new type starts from zero.
            onValueChange={([type]: string[]) => {
              if (type && type !== rule.type)
                update({ ...rule, type: type as RDVSurplusType, value: 0 })
            }}
          >
            {RDV_SURPLUS_TYPES.map((type) => (
              <ToggleGroupItem key={type} value={type}>
                {t(`returns_refunds.refunds.rdv_surplus_types.${type}`)}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          {rule.type === 'FIXED' ? (
            <DecimalInput
              key="FIXED"
              size="sm"
              className="w-24"
              prefix={currencySymbol('EUR', i18n.language)}
              aria-label={t('returns_refunds.refunds.rdv_surplus_amount_for', {
                n
              })}
              disabled={readOnly}
              value={rule.value / 100}
              parse={(text) => parseSurplusAmount(text) / 100}
              format={(euros) => euros.toFixed(2)}
              onChange={(euros) =>
                update({ ...rule, value: Math.round(euros * 100) })
              }
            />
          ) : (
            <>
              <DecimalInput
                key="PERCENTAGE"
                size="sm"
                className="w-24"
                aria-label={t(
                  'returns_refunds.refunds.rdv_surplus_percentage_for',
                  { n }
                )}
                disabled={readOnly}
                value={rule.value}
                parse={parseSurplusPercentage}
                onChange={(value) => update({ ...rule, value })}
              />
              <span className="text-xs text-neutral-600">%</span>
            </>
          )}
        </FieldRoot>
      )
    }
  ]
  return (
    <>
      <SettingsSection
        title={methodsLabel}
        description={t('returns_refunds.refunds.methods_description')}
      >
        <RuleTable
          label={methodsLabel}
          rules={value.methods}
          saved={saved.methods}
          context="RefundMethods"
          readOnly={readOnly}
          columns={columns(
            t('returns_refunds.refunds.method'),
            options(
              REFUND_CONFIGURATION_METHODS,
              'returns_refunds.refunds.methods'
            )
          )}
          addLabel={t('returns_refunds.refunds.add_method')}
          newRule={() => ({
            target: 'ORIGINAL',
            timing: 'ON_ITEM_VERIFIED',
            whens: []
          })}
          onChange={(methods) => onChange({ ...value, methods })}
        />
      </SettingsSection>
      {value.rdvSurplus && (
        <SettingsSection
          title={surplusLabel}
          description={t('returns_refunds.refunds.rdv_surplus_description')}
        >
          <RuleTable
            label={surplusLabel}
            rules={value.rdvSurplus}
            saved={saved.rdvSurplus ?? []}
            context="RDVSurplus"
            readOnly={readOnly}
            columns={surplusColumns}
            addLabel={t('returns_refunds.refunds.add_rdv_surplus')}
            emptyText={t('returns_refunds.refunds.no_rdv_surplus')}
            newRule={(): RDVSurplusRule => ({
              type: 'FIXED',
              value: 0,
              whens: []
            })}
            onChange={(rdvSurplus) => onChange({ ...value, rdvSurplus })}
          />
        </SettingsSection>
      )}
      <SettingsSection
        title={actionsLabel}
        description={t('returns_refunds.refunds.actions_description')}
      >
        <RuleTable
          label={actionsLabel}
          rules={value.actions}
          saved={saved.actions}
          // IsReturnEnabled, which evaluates the return actions, offers the same fields.
          context="IsExchangeEnabled"
          readOnly={readOnly}
          columns={columns(
            t('returns_refunds.refunds.action'),
            options(
              REFUND_CONFIGURATION_ACTIONS,
              'returns_refunds.refunds.actions'
            ),
            (target) => target === 'TO_RETURN'
          )}
          addLabel={t('returns_refunds.refunds.add_action')}
          newRule={() => ({
            target: 'TO_RETURN',
            timing: 'ON_ITEM_VERIFIED',
            whens: []
          })}
          onChange={(actions) => onChange({ ...value, actions })}
        />
      </SettingsSection>
      <SettingsSection title={t('returns_refunds.refunds.discounts_title')}>
        <SettingsPanel>
          {DISCOUNTS.map((key) => (
            <SwitchRow
              key={key}
              label={t(`returns_refunds.refunds.${key}`)}
              description={t(`returns_refunds.refunds.${key}_description`)}
              checked={value[key]}
              readOnly={readOnly}
              onChange={(checked) => onChange({ ...value, [key]: checked })}
            />
          ))}
        </SettingsPanel>
      </SettingsSection>
    </>
  )
}
